import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import { listConferences, createConference } from '@/services/conferencesService';

const BLUE = '#002169';

const STATUS_STYLES = {
  planning:  { bg: '#eff6ff', color: '#1d4ed8', label: 'Planning' },
  confirmed: { bg: '#f0fdf4', color: '#15803d', label: 'Confirmed' },
  done:      { bg: '#f3f4f6', color: '#6b7280', label: 'Done' },
  cancelled: { bg: '#fef2f2', color: '#dc2626', label: 'Cancelled' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = pct === 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <div style={{ width: 80, height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{done} / {total} done</span>
    </div>
  );
}

const ConferencesPage = () => {
  const navigate = useNavigate();
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', location: '', notes: '', status: 'planning' });
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listConferences();
      setConferences(res?.conferences || []);
    } catch { setError('Failed to load conferences'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? conferences : conferences.filter(c => c.status === filter);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) { setFormError('Name, start date, and end date are required.'); return; }
    setSaving(true);
    setFormError(null);
    try {
      const res = await createConference(form);
      setShowModal(false);
      navigate(`/admin-functions/conferences/${res.id}`);
    } catch { setFormError('Failed to create conference. Please try again.'); }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-white p-6 min-w-0 max-w-full">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ color: BLUE }} className="text-2xl font-extrabold">Conferences</h1>
        <button
          onClick={() => { setShowModal(true); setFormError(null); setForm({ name: '', start_date: '', end_date: '', location: '', notes: '', status: 'planning' }); }}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white rounded-lg transition"
          style={{ background: BLUE }}
        >
          <AddIcon style={{ fontSize: 18 }} /> New Conference
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {['all', 'planning', 'confirmed', 'done'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className="px-3 py-1 rounded-full text-xs font-semibold transition"
            style={{ background: filter === f ? BLUE : '#f3f4f6', color: filter === f ? '#fff' : '#374151' }}>
            {f === 'all' ? 'All' : STATUS_STYLES[f]?.label}
            {f !== 'all' && ` (${conferences.filter(c => c.status === f).length})`}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-12"><CircularProgress style={{ color: BLUE }} /></div>}
      {error && <div className="text-red-500 text-sm py-4">{error}</div>}

      {!loading && !error && (
        filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <EventIcon style={{ fontSize: 40, marginBottom: 8 }} />
            <p className="font-semibold">No conferences yet</p>
            <p className="text-sm">Click "+ New Conference" to get started</p>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))' }}>
            {filtered.map(conf => {
              const s = STATUS_STYLES[conf.status] || STATUS_STYLES.planning;
              return (
                <div key={conf.id}
                  onClick={() => navigate(`/admin-functions/conferences/${conf.id}`)}
                  className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-amber-400 hover:shadow-sm transition">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                    <EventIcon style={{ color: s.color, fontSize: 20 }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm" style={{ color: BLUE }}>{conf.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {fmtDate(conf.start_date)} – {fmtDate(conf.end_date)}
                      {conf.location && <span className="ml-2">· {conf.location}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                    <ProgressBar done={conf.progress_done} total={conf.progress_total} />
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* New Conference Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 rounded-t-2xl" style={{ background: BLUE }}>
              <h2 className="text-base font-bold text-white">New Conference</h2>
              <button onClick={() => setShowModal(false)} className="text-white/60 hover:text-white"><CloseIcon /></button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Conference Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. APP2026 Annual Conference"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Start Date *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">End Date *</label>
                  <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Location</label>
                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Melbourne Convention Centre"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
                  <option value="planning">Planning</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Any initial notes…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none" />
              </div>
              {formError && <p className="text-xs text-red-500">{formError}</p>}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 text-white rounded-lg text-sm font-bold hover:opacity-90 disabled:opacity-50 transition" style={{ background: BLUE }}>
                  {saving ? 'Creating…' : 'Create & Open'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferencesPage;
